import React, { useState } from "react";
import { useSelector } from "react-redux";
import { Link } from "react-router-dom";

import SupportService from "api/Support/SupportService";
import FinishDialog from "components/FinishDialog";
import Header from "components/Header";
import TextField from "components/TextField";
import TextAreaField from "components/TextAreaField";
import { UserState } from "store/userSlice";
import { RootState } from "store";
import { AppRoutes } from "pages/constants/routes";

import styles from "./Support.module.scss";
import { ReactComponent as Warning } from "assets/misc/warning_circle.svg";

interface ValidationErrors {
  email?: string;
  topic?: string;
  body?: string;
}

const Support = () => {
  const userState: UserState = useSelector((state: RootState) => state.user);

  const [email, setEmail] = useState<string>("");
  const [topic, setTopic] = useState<string>("");
  const [body, setBody] = useState<string>("");

  const minBodyLength = 100;
  const maxBodyLength = 2000;

  const [errors, setErrors] = useState<ValidationErrors>({});

  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [isSuccessDialogOpen, setIsSuccessDialogOpen] =
    useState<boolean>(false);

  const validateEmail = (email: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const validateForm = () => {
    let isPass = true;
    if (!userState.loggedIn && !validateEmail(email)) {
      isPass = false;
      setErrors((errors) => ({
        ...errors,
        email: "Invalid email format",
      }));
    }

    if (topic.trim().length <= 0) {
      isPass = false;
      setErrors((errors) => ({
        ...errors,
        topic: "Please include a topic",
      }));
    }

    if (
      body.trim().length < minBodyLength ||
      body.trim().length > maxBodyLength
    ) {
      isPass = false;
      setErrors((errors) => ({
        ...errors,
        body: "Please write a sufficient description",
      }));
    }

    return isPass;
  };

  const onFormChange =
    (onChange: (newValue: string) => void) => (newValue: string) => {
      setErrors({});
      onChange(newValue);
    };

  const clearForm = () => {
    setEmail("");
    setTopic("");
    setBody("");
  };

  const onSubmit = async () => {
    if (!validateForm() || isSubmitting) {
      return;
    }

    setIsSubmitting(true);
    try {
      await SupportService.newSupportTicket(email, topic, body);
      clearForm();
      setIsSuccessDialogOpen(true);
    } catch (err) {
      // handle error
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className={styles.page}>
      <div className={styles.background} />
      <Header />
      <div className={styles.content}>
        <div className={styles.card}>
          <div className={styles.header}>
            <div className={styles.title}>Let us help you out!</div>
            <div className={styles.subtitle}>We just need some details</div>
          </div>
          <div className={styles.form}>
            {!userState.loggedIn && (
              <div className={styles.signInBanner}>
                <Warning />
                <span>
                  If you are an existing user please{" "}
                  <Link to={AppRoutes.signin}>sign in</Link> before contacting
                  us.
                </span>
              </div>
            )}
            <div className={styles.textFieldWrapper}>
              {userState.loggedIn ? (
                <TextField
                  horizontal
                  disabled
                  label="Email"
                  value={userState.email}
                  // eslint-disable-next-line @typescript-eslint/no-empty-function
                  setValue={() => {}}
                />
              ) : (
                <TextField
                  autoFocus
                  horizontal
                  label="Email"
                  value={email}
                  setValue={onFormChange(setEmail)}
                  error={errors.email}
                />
              )}
            </div>
            <div className={styles.textFieldWrapper}>
              <TextField
                autoFocus={userState.loggedIn}
                horizontal
                label="Topic"
                value={topic}
                setValue={onFormChange(setTopic)}
                error={errors.topic}
              />
            </div>
            <TextAreaField
              label="Please decribe how we can help you"
              value={body}
              setValue={onFormChange(setBody)}
              minLength={minBodyLength}
              maxLength={maxBodyLength}
              error={errors.body !== undefined}
            />
          </div>
          <div>
            <div className={styles.submitContainer}>
              <div
                className={`${styles.submitButton} ${isSubmitting && styles.disabled}`}
                onClick={onSubmit}
              >
                Send
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Success Dialog for successful ticket creation */}
      <FinishDialog
        isOpen={isSuccessDialogOpen}
        setIsOpen={setIsSuccessDialogOpen}
        title="We Received It!"
        message="We will get back to you as soon as we can! You should receive an email to your mailbox soon."
      />
    </div>
  );
};

export default Support;
